import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { api, errorText } from "../api";
import { useIsFocused } from "../hooks";
import { toast } from "../toast";
import type {
  FrameState,
  TuberAnimation,
  TuberIdle,
  TuberState,
  TuberStep,
} from "../types";

export const DEFAULT_ANIMATION: TuberAnimation = {
  rest_frame: "closed",
  talk_mode: "cycle",
  talk_frames: [{ frame: "open", hold_ms: 0 }],
  talk_frame_ms: 140,
  talk_hold_ms: 90,
  idle_min_ms: 2500,
  idle_max_ms: 9000,
  idles: [],
  bounce: 1,
  tilt: 0,
  pulse: 0,
  breathe: 0,
};

/* How the whole character moves, as opposed to which frame it shows. The hop
   is the one every character has always had; the other three are off until an
   admin turns them up. Ranges match what the server clamps to. */
const MOTION_KNOBS: {
  key: "bounce" | "tilt" | "pulse" | "breathe";
  label: string;
  hint: string;
  max: number;
}[] = [
  { key: "bounce", label: "Bounce", hint: "Hops on loud syllables", max: 2.5 },
  { key: "tilt", label: "Sway", hint: "Rocks while talking", max: 3 },
  { key: "pulse", label: "Squash", hint: "Swells when loud", max: 3 },
  { key: "breathe", label: "Breathe", hint: "Moves at rest", max: 3 },
];

/* The overlay's arithmetic, copied rather than approximated: the preview is
   only worth looking at if it moves the way the browser source will. See
   tuberMotion() in static/overlay.html. */
function motionTransform(
  animation: TuberAnimation,
  now: number,
  loud: number,
  breathMix: number,
): string {
  const bounce = loud * animation.bounce;
  const tilt = Math.sin(now / 260) * loud * (animation.tilt ?? 0) * 4;
  const pulse = loud * loud * (animation.pulse ?? 0);
  const breathe = Math.sin(now / 1100) * (animation.breathe ?? 0) * breathMix;
  return (
    `translateY(${-bounce * 22}px) rotate(${tilt}deg) ` +
    `scale(${1 + pulse * 0.05 + breathe * 0.015}) ` +
    `scaleY(${1 + bounce * 0.03}) scaleX(${1 - bounce * 0.015})`
  );
}

interface Props {
  tuber: TuberState;
  presetId?: number;
  presetName?: string;
  onState: (state: TuberState) => void;
}

/**
 * Compact summary and quick controls for the sidebar or preset card,
 * with a button to open the full animation dialog.
 */
export function TuberControls({
  tuber,
  presetId,
  presetName,
  onState,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [sizeFocused, sizeFocus] = useIsFocused();
  const [offsetFocused, offsetFocus] = useIsFocused();
  const [size, setSize] = useState(tuber.size ?? 46);
  const [side, setSide] = useState(tuber.side ?? "right");
  const [offset, setOffset] = useState(tuber.offset ?? 0);

  const pushTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!sizeFocused) setSize(tuber.size ?? 46);
  }, [tuber.size, sizeFocused]);

  useEffect(() => {
    setSide(tuber.side ?? "right");
  }, [tuber.side]);

  useEffect(() => {
    if (!offsetFocused) setOffset(tuber.offset ?? 0);
  }, [tuber.offset, offsetFocused]);

  useEffect(() => () => clearTimeout(pushTimer.current), []);

  const animation = tuber.animation ?? DEFAULT_ANIMATION;
  const frames: FrameState[] =
    tuber.frames && tuber.frames.length > 0
      ? tuber.frames
      : [
          {
            id: "closed",
            custom: !!tuber.custom?.closed,
            version: tuber.version || "0",
          },
          {
            id: "open",
            custom: !!tuber.custom?.open,
            version: tuber.version || "0",
          },
        ];

  function getFrameSrc(frameId: string) {
    const found = frames.find((f) => f.id === frameId);
    const ver = found?.version || tuber.version || "0";
    const stageQuery = presetId ? `stage=${presetId}&` : "";
    return `/tuber/${frameId}.png?${stageQuery}v=${ver}`;
  }

  function pushLayout(nextSize = size, nextSide = side, nextOffset = offset) {
    clearTimeout(pushTimer.current);
    pushTimer.current = setTimeout(async () => {
      try {
        if (presetId) {
          const res = await api<{ tuber?: TuberState } | TuberState>(
            `/api/admin/presets/${presetId}/overlay`,
            {
              method: "POST",
              body: JSON.stringify({
                size: nextSize,
                side: nextSide,
                offset: nextOffset,
                animation,
              }),
            },
          );
          if ("tuber" in res && res.tuber) {
            onState(res.tuber);
          } else {
            onState(res as TuberState);
          }
        } else {
          const updated = await api<TuberState>("/api/admin/tuber", {
            method: "POST",
            body: JSON.stringify({
              size: nextSize,
              side: nextSide,
              animation,
            }),
          });
          onState(updated);
        }
      } catch (error) {
        toast(errorText(error), "error");
      }
    }, 120);
  }

  return (
    <div className="tuber-compact-card">
      <div className="tuber-compact-header">
        <div
          className="tuber-compact-thumb"
          onClick={() => setModalOpen(true)}
          title="Edit animation"
        >
          <img
            src={getFrameSrc(animation.rest_frame || "closed")}
            alt="Character preview"
          />
        </div>
        <div className="tuber-compact-info">
          <div className="tuber-compact-title">
            PNGtuber
          </div>
          <div className="tuber-compact-meta">
            <span>Rest: <code>{animation.rest_frame}</code></span>
            <span>·</span>
            <span>{animation.talk_mode === "cycle" ? "Flipbook" : "Loudness"}</span>
            <span>·</span>
            <span>{frames.length} frames</span>
            {animation.idles.length > 0 && (
              <>
                <span>·</span>
                <span>{animation.idles.length} idles</span>
              </>
            )}
          </div>
          <button
            type="button"
            className="ghost"
            style={{ marginTop: 6, alignSelf: "flex-start" }}
            onClick={() => setModalOpen(true)}
          >
            🎨 Edit Animation & Idle States...
          </button>
        </div>
      </div>

      {/* Quick Layout Sliders */}
      <div className="slider" style={{ marginTop: 12, marginBottom: 4 }}>
        <label>
          <span>Character size</span>
          <span className="slider-val">{Math.round(size)}vh</span>
        </label>
        <input
          type="range"
          min={10}
          max={100}
          step={1}
          value={size}
          {...sizeFocus}
          onChange={(e) => {
            const next = parseFloat(e.target.value);
            setSize(next);
            pushLayout(next, side, offset);
          }}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <div className="field-label">Position</div>
        <select
          value={side}
          onChange={(e) => {
            const next = e.target.value as TuberState["side"];
            setSide(next);
            pushLayout(size, next, offset);
          }}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>

      {presetId !== undefined && (
        <div className="slider" style={{ marginTop: 10 }}>
          <label>
            <span>Offset</span>
            <span className="slider-val">{Math.round(offset)}vw</span>
          </label>
          <input
            type="range"
            min={-40}
            max={40}
            step={1}
            value={offset}
            {...offsetFocus}
            onChange={(e) => {
              const next = parseFloat(e.target.value);
              setOffset(next);
              pushLayout(size, side, next);
            }}
          />
        </div>
      )}

      {/* Dedicated Spacious Modal Dialog */}
      {modalOpen && (
        <TuberModal
          tuber={tuber}
          presetId={presetId}
          presetName={presetName}
          onClose={() => setModalOpen(false)}
          onState={onState}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Dedicated Spacious Animation & Frames Modal Dialog
// -----------------------------------------------------------------------------

interface ModalProps {
  tuber: TuberState;
  presetId?: number;
  presetName?: string;
  onClose: () => void;
  onState: (state: TuberState) => void;
}

type Tab = "talk" | "idles" | "frames";

function TuberModal({
  tuber,
  presetId,
  presetName,
  onClose,
  onState,
}: ModalProps) {
  const [tab, setTab] = useState<Tab>("talk");
  const [animation, setAnimation] = useState<TuberAnimation>(
    tuber.animation ?? DEFAULT_ANIMATION,
  );
  const [uploading, setUploading] = useState(false);
  const [testingIdleIndex, setTestingIdleIndex] = useState<number | null>(null);

  // Drag & drop state for talk steps and idle steps
  const [draggedStepIdx, setDraggedStepIdx] = useState<number | null>(null);
  const [dragOverStepIdx, setDragOverStepIdx] = useState<number | null>(null);

  const pushTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (tuber.animation) {
      setAnimation(tuber.animation);
    }
  }, [tuber.animation]);

  useEffect(() => () => clearTimeout(pushTimer.current), []);

  // Escape key to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const frames: FrameState[] =
    tuber.frames && tuber.frames.length > 0
      ? tuber.frames
      : [
          {
            id: "closed",
            custom: !!tuber.custom?.closed,
            version: tuber.version || "0",
          },
          {
            id: "open",
            custom: !!tuber.custom?.open,
            version: tuber.version || "0",
          },
        ];

  function getFrameSrc(frameId: string) {
    const found = frames.find((f) => f.id === frameId);
    const ver = found?.version || tuber.version || "0";
    const stageQuery = presetId ? `stage=${presetId}&` : "";
    return `/tuber/${frameId}.png?${stageQuery}v=${ver}`;
  }

  function pushChanges(nextAnimation: TuberAnimation) {
    clearTimeout(pushTimer.current);
    pushTimer.current = setTimeout(async () => {
      try {
        if (presetId) {
          const res = await api<{ tuber?: TuberState } | TuberState>(
            `/api/admin/presets/${presetId}/overlay`,
            {
              method: "POST",
              body: JSON.stringify({
                animation: nextAnimation,
              }),
            },
          );
          if ("tuber" in res && res.tuber) {
            onState(res.tuber);
          } else {
            onState(res as TuberState);
          }
        } else {
          const updated = await api<TuberState>("/api/admin/tuber", {
            method: "POST",
            body: JSON.stringify({
              animation: nextAnimation,
            }),
          });
          onState(updated);
        }
      } catch (error) {
        toast(errorText(error), "error");
      }
    }, 120);
  }

  function updateAnimation(patch: Partial<TuberAnimation>) {
    const next = { ...animation, ...patch };
    setAnimation(next);
    pushChanges(next);
  }

  // --- Frame upload / replace / delete ---
  async function uploadNewFrame(file: File | undefined) {
    if (!file) return;
    if (file.type !== "image/png") {
      toast("Transparent PNG only", "error");
      return;
    }
    setUploading(true);
    try {
      const endpoint = presetId
        ? `/api/admin/presets/${presetId}/tuber/frames`
        : "/api/admin/tuber/frames";
      const result = await api<TuberState & { frame?: string }>(endpoint, {
        method: "POST",
        headers: { "Content-Type": "image/png" },
        body: file,
      });
      onState(result);
      toast(`Frame added: ${result.frame || "new frame"}`, "ok");
    } catch (error) {
      toast(errorText(error), "error");
    } finally {
      setUploading(false);
    }
  }

  async function replaceFrame(frameId: string, file: File | undefined) {
    if (!file) return;
    if (file.type !== "image/png") {
      toast("Transparent PNG only", "error");
      return;
    }
    try {
      const endpoint = presetId
        ? `/api/admin/presets/${presetId}/tuber/${frameId}`
        : `/api/admin/tuber/${frameId}`;
      const result = await api<TuberState>(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "image/png" },
        body: file,
      });
      onState(result);
      toast(`Frame '${frameId}' replaced`, "ok");
    } catch (error) {
      toast(errorText(error), "error");
    }
  }

  async function deleteOrResetFrame(frameId: string) {
    const isBundled = frameId === "closed" || frameId === "open";
    const msg = isBundled
      ? `Reset '${frameId}' to default?`
      : `Delete frame '${frameId}'?`;
    if (!confirm(msg)) return;
    try {
      const endpoint = presetId
        ? `/api/admin/presets/${presetId}/tuber/${frameId}`
        : `/api/admin/tuber/${frameId}`;
      const result = await api<TuberState>(endpoint, { method: "DELETE" });
      onState(result);
      toast(isBundled ? `'${frameId}' reset` : `'${frameId}' deleted`, "ok");
    } catch (error) {
      toast(errorText(error), "error");
    }
  }

  // --- Talk cycle helpers ---
  function addTalkStep(frameId: string) {
    const nextSteps: TuberStep[] = [
      ...animation.talk_frames,
      { frame: frameId, hold_ms: 0 },
    ];
    updateAnimation({ talk_frames: nextSteps });
  }

  function updateTalkStep(index: number, patch: Partial<TuberStep>) {
    const nextSteps = animation.talk_frames.map((s, i) =>
      i === index ? { ...s, ...patch } : s,
    );
    updateAnimation({ talk_frames: nextSteps });
  }

  function removeTalkStep(index: number) {
    if (animation.talk_frames.length <= 1) {
      toast("Needs at least one frame", "error");
      return;
    }
    const nextSteps = animation.talk_frames.filter((_, i) => i !== index);
    updateAnimation({ talk_frames: nextSteps });
  }

  function reorderTalkSteps(from: number, to: number) {
    if (from === to) return;
    const nextSteps = [...animation.talk_frames];
    const [moved] = nextSteps.splice(from, 1);
    nextSteps.splice(to, 0, moved);
    updateAnimation({ talk_frames: nextSteps });
  }

  // --- Idle actions helpers ---
  function addIdleAction() {
    const defaultFrame = frames[0]?.id || "closed";
    const newIdle: TuberIdle = {
      name: `Idle ${animation.idles.length + 1}`,
      steps: [
        { frame: frames.find((f) => f.id !== defaultFrame)?.id || defaultFrame, hold_ms: 0 },
        { frame: defaultFrame, hold_ms: 0 },
      ],
      step_ms: 90,
      weight: 1,
      while_talking: false,
    };
    updateAnimation({ idles: [...animation.idles, newIdle] });
  }

  function updateIdleAction(index: number, patch: Partial<TuberIdle>) {
    const nextIdles = animation.idles.map((idle, i) =>
      i === index ? { ...idle, ...patch } : idle,
    );
    updateAnimation({ idles: nextIdles });
  }

  function removeIdleAction(index: number) {
    const nextIdles = animation.idles.filter((_, i) => i !== index);
    updateAnimation({ idles: nextIdles });
  }

  function addIdleStep(idleIndex: number, frameId: string) {
    const idle = animation.idles[idleIndex];
    if (!idle) return;
    const nextSteps: TuberStep[] = [...idle.steps, { frame: frameId, hold_ms: 0 }];
    updateIdleAction(idleIndex, { steps: nextSteps });
  }

  function updateIdleStep(idleIndex: number, stepIndex: number, patch: Partial<TuberStep>) {
    const idle = animation.idles[idleIndex];
    if (!idle) return;
    const nextSteps = idle.steps.map((s, i) =>
      i === stepIndex ? { ...s, ...patch } : s,
    );
    updateIdleAction(idleIndex, { steps: nextSteps });
  }

  function removeIdleStep(idleIndex: number, stepIndex: number) {
    const idle = animation.idles[idleIndex];
    if (!idle || idle.steps.length <= 1) {
      toast("Needs at least one step", "error");
      return;
    }
    const nextSteps = idle.steps.filter((_, i) => i !== stepIndex);
    updateIdleAction(idleIndex, { steps: nextSteps });
  }

  function reorderIdleSteps(idleIndex: number, from: number, to: number) {
    if (from === to) return;
    const idle = animation.idles[idleIndex];
    if (!idle) return;
    const nextSteps = [...idle.steps];
    const [moved] = nextSteps.splice(from, 1);
    nextSteps.splice(to, 0, moved);
    updateIdleAction(idleIndex, { steps: nextSteps });
  }

  return (
    <div
      className="tuber-modal-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="tuber-modal" role="dialog" aria-modal="true">
        {/* Modal Header */}
        <div className="tuber-modal-head">
          <div className="tuber-modal-title">
            <h2>{presetName ? `${presetName}: animation` : "Animation"}</h2>
          </div>
          <button type="button" className="ghost" onClick={onClose}>
            Done
          </button>
        </div>

        {/* Modal Body */}
        <div className="tuber-modal-body">
          {/* Live Interactive Preview Canvas */}
          <TuberLivePreview
            animation={animation}
            frames={frames}
            getFrameSrc={getFrameSrc}
            testIdleAction={
              testingIdleIndex !== null ? animation.idles[testingIdleIndex] : null
            }
            onTestIdleDone={() => setTestingIdleIndex(null)}
          />

          {/* Modal Tabs */}
          <div className="tabs" role="tablist" style={{ marginTop: 14 }}>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "talk"}
              onClick={() => setTab("talk")}
            >
              {/* a bare count, like the two tabs beside it — and "1 steps" was
                  wrong every time a cycle had a single frame */}
              Talking ({animation.talk_frames.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "idles"}
              onClick={() => setTab("idles")}
            >
              Idle ({animation.idles.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "frames"}
              onClick={() => setTab("frames")}
            >
              Frames ({frames.length})
            </button>
          </div>

          {/* Missing Frames Warning */}
          {tuber.missing && tuber.missing.length > 0 && (
            <div className="tuber-missing-alert">
              <b>Missing frames:</b> {tuber.missing.join(", ")}
            </div>
          )}

          {/* TAB 1: TALKING CYCLE */}
          {tab === "talk" && (
            <div className="tuber-tab-pane">
              <div className="field-row" style={{ alignItems: "center", gap: 16 }}>
                <label style={{ flex: 1 }}>
                  <span className="field-label" style={{ display: "block" }}>
                    Rest frame
                  </span>
                  <select
                    value={animation.rest_frame}
                    onChange={(e) => updateAnimation({ rest_frame: e.target.value })}
                  >
                    {frames.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.id} {f.custom ? "(custom)" : "(bundled)"}
                      </option>
                    ))}
                  </select>
                </label>

                <div style={{ flex: 1 }}>
                  <span className="field-label" style={{ display: "block" }}>
                    Mode
                  </span>
                  <div className="tuber-mode-toggle">
                    <button
                      type="button"
                      className={animation.talk_mode === "cycle" ? "active" : "ghost"}
                      onClick={() => updateAnimation({ talk_mode: "cycle" })}
                    >
                      Flipbook
                    </button>
                    <button
                      type="button"
                      className={animation.talk_mode === "loudness" ? "active" : "ghost"}
                      onClick={() => updateAnimation({ talk_mode: "loudness" })}
                    >
                      Loudness
                    </button>
                  </div>
                </div>
              </div>

              <div className="field-row" style={{ marginTop: 8, gap: 16 }}>
                <div className="slider" style={{ flex: 1, margin: 0 }}>
                  <label>
                    <span>Talk frame speed</span>
                    <span className="slider-val">
                      {Math.round(animation.talk_frame_ms)}ms (≈
                      {(1000 / Math.max(animation.talk_frame_ms, 20)).toFixed(1)} FPS)
                    </span>
                  </label>
                  <input
                    type="range"
                    min={20}
                    max={1000}
                    step={10}
                    value={animation.talk_frame_ms}
                    onChange={(e) =>
                      updateAnimation({ talk_frame_ms: parseFloat(e.target.value) })
                    }
                  />
                  
                </div>

                <div className="slider" style={{ flex: 1, margin: 0 }}>
                  <label>
                    <span>Talk hold time</span>
                    <span className="slider-val">{Math.round(animation.talk_hold_ms)}ms</span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={500}
                    step={10}
                    value={animation.talk_hold_ms}
                    onChange={(e) =>
                      updateAnimation({ talk_hold_ms: parseFloat(e.target.value) })
                    }
                  />
                  
                </div>
              </div>

              <div className="motion-grid">
                {MOTION_KNOBS.map((knob) => {
                  const value = animation[knob.key] ?? 0;
                  return (
                    <div className="slider" key={knob.key}>
                      <label htmlFor={`motion-${knob.key}`}>
                        <span>{knob.label}</span>
                        <span className="slider-val">
                          {value === 0 ? "off" : `${value.toFixed(1)}x`}
                        </span>
                      </label>
                      <input
                        id={`motion-${knob.key}`}
                        type="range"
                        min={0}
                        max={knob.max}
                        step={0.1}
                        value={value}
                        aria-describedby={`motion-${knob.key}-hint`}
                        onChange={(e) =>
                          updateAnimation({ [knob.key]: parseFloat(e.target.value) })
                        }
                      />
                      <div className="hint" id={`motion-${knob.key}-hint`}>
                        {knob.hint}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Drag-and-drop Sequence Editor */}
              <div className="tuber-steps-editor">
                <div className="section-head" style={{ marginBottom: 10 }}>
                  <div>
                    <h3>
                      {animation.talk_mode === "cycle"
                        ? "Frames"
                        : "Levels (quiet to loud)"}
                    </h3>
                  </div>
                  <select
                    className="tuber-add-step-select"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) addTalkStep(e.target.value);
                    }}
                  >
                    <option value="">+ Add frame</option>
                    {frames.map((f) => (
                      <option key={f.id} value={f.id}>
                        + {f.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="tuber-steps-list">
                  {animation.talk_frames.map((step, idx) => (
                    <div
                      key={idx}
                      className={`tuber-step-card ${
                        draggedStepIdx === idx ? "dragging" : ""
                      } ${dragOverStepIdx === idx ? "drag-over" : ""}`}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        setDraggedStepIdx(idx);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (dragOverStepIdx !== idx) setDragOverStepIdx(idx);
                      }}
                      onDragLeave={() => {
                        if (dragOverStepIdx === idx) setDragOverStepIdx(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggedStepIdx !== null && draggedStepIdx !== idx) {
                          reorderTalkSteps(draggedStepIdx, idx);
                        }
                        setDraggedStepIdx(null);
                        setDragOverStepIdx(null);
                      }}
                      onDragEnd={() => {
                        setDraggedStepIdx(null);
                        setDragOverStepIdx(null);
                      }}
                    >
                      <span className="tuber-drag-handle" title="Drag to rearrange">
                        ⋮⋮
                      </span>
                      <div className="tuber-step-num">{idx + 1}</div>
                      <div className="tuber-step-thumb">
                        <img src={getFrameSrc(step.frame)} alt={step.frame} />
                      </div>
                      <div className="tuber-step-details">
                        <select
                          value={step.frame}
                          onChange={(e) =>
                            updateTalkStep(idx, { frame: e.target.value })
                          }
                        >
                          {frames.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.id}
                            </option>
                          ))}
                        </select>
                        {animation.talk_mode === "cycle" && (
                          <label className="tuber-step-hold">
                            <span>Hold:</span>
                            <input
                              type="number"
                              min={0}
                              max={5000}
                              step={10}
                              placeholder="Auto"
                              value={step.hold_ms || ""}
                              title="Blank uses the default speed"
                              onChange={(e) =>
                                updateTalkStep(idx, {
                                  hold_ms: parseFloat(e.target.value) || 0,
                                })
                              }
                            />
                            <span className="unit">ms</span>
                          </label>
                        )}
                      </div>
                      <div className="tuber-step-actions">
                        <button
                          type="button"
                          className="ghost"
                          disabled={idx === 0}
                          onClick={() => reorderTalkSteps(idx, idx - 1)}
                          title="Move frame up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          disabled={idx === animation.talk_frames.length - 1}
                          onClick={() => reorderTalkSteps(idx, idx + 1)}
                          title="Move frame down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="ghost danger"
                          onClick={() => removeTalkStep(idx)}
                          title="Remove from cycle"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: IDLE STATES (RANDOM BLINKING, ETC.) */}
          {tab === "idles" && (
            <div className="tuber-tab-pane">
              <div className="tuber-global-idle-card">
                <div className="field-label" style={{ fontWeight: 600 }}>
                  Idle timing
                </div>
                <div className="field-row" style={{ gap: 16 }}>
                  <div className="slider" style={{ flex: 1, margin: 0 }}>
                    <label>
                      <span>Min gap</span>
                      <span className="slider-val">{(animation.idle_min_ms / 1000).toFixed(1)}s</span>
                    </label>
                    <input
                      type="range"
                      min={200}
                      max={30000}
                      step={200}
                      value={animation.idle_min_ms}
                      onChange={(e) => {
                        const min = parseFloat(e.target.value);
                        updateAnimation({
                          idle_min_ms: min,
                          idle_max_ms: Math.max(animation.idle_max_ms, min),
                        });
                      }}
                    />
                  </div>
                  <div className="slider" style={{ flex: 1, margin: 0 }}>
                    <label>
                      <span>Max gap</span>
                      <span className="slider-val">{(animation.idle_max_ms / 1000).toFixed(1)}s</span>
                    </label>
                    <input
                      type="range"
                      min={200}
                      max={60000}
                      step={500}
                      value={animation.idle_max_ms}
                      onChange={(e) => {
                        const max = parseFloat(e.target.value);
                        updateAnimation({
                          idle_max_ms: max,
                          idle_min_ms: Math.min(animation.idle_min_ms, max),
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              <div
                className="section-head"
                style={{ marginTop: 16, marginBottom: 8, alignItems: "center" }}
              >
                <div>
                  <h3>Idle animations ({animation.idles.length})</h3>
                </div>
                <button
                  type="button"
                  className="button-like"
                  disabled={animation.idles.length >= 12}
                  onClick={addIdleAction}
                >
                  + Add
                </button>
              </div>

              {animation.idles.length === 0 ? (
                <div className="hint" style={{ padding: "20px 0", textAlign: "center" }}>
                  No idle animations.
                </div>
              ) : (
                <div className="tuber-idles-list">
                  {animation.idles.map((idle, idleIdx) => (
                    <div key={idleIdx} className="tuber-idle-card">
                      <div className="tuber-idle-header">
                        <input
                          type="text"
                          maxLength={24}
                          className="tuber-idle-name-input"
                          value={idle.name}
                          placeholder="Blink"
                          onChange={(e) =>
                            updateIdleAction(idleIdx, { name: e.target.value })
                          }
                        />
                        <span className="spacer" />
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => setTestingIdleIndex(idleIdx)}
                          title="Preview"
                        >
                          ▶ Test
                        </button>
                        <button
                          type="button"
                          className="ghost danger"
                          onClick={() => removeIdleAction(idleIdx)}
                          
                        >
                          Delete
                        </button>
                      </div>

                      <div className="tuber-idle-config-row">
                        <div className="slider" style={{ margin: 0 }}>
                          <label>
                            <span>Weight</span>
                            <span className="slider-val">{idle.weight.toFixed(1)}x</span>
                          </label>
                          <input
                            type="range"
                            min={0}
                            max={5}
                            step={0.1}
                            value={idle.weight}
                            onChange={(e) =>
                              updateIdleAction(idleIdx, {
                                weight: parseFloat(e.target.value),
                              })
                            }
                          />
                          <small className="hint">
                            {idle.weight === 0 ? "0 = Disabled" : "Relative frequency"}
                          </small>
                        </div>

                        <div className="slider" style={{ margin: 0 }}>
                          <label>
                            <span>Step speed</span>
                            <span className="slider-val">{Math.round(idle.step_ms)}ms</span>
                          </label>
                          <input
                            type="range"
                            min={20}
                            max={1000}
                            step={10}
                            value={idle.step_ms}
                            onChange={(e) =>
                              updateIdleAction(idleIdx, {
                                step_ms: parseFloat(e.target.value),
                              })
                            }
                          />
                          
                        </div>

                        <label className="check" style={{ alignSelf: "center", marginTop: 4 }}>
                          <input
                            type="checkbox"
                            checked={idle.while_talking}
                            onChange={(e) =>
                              updateIdleAction(idleIdx, {
                                while_talking: e.target.checked,
                              })
                            }
                          />
                          <span>Play while speaking</span>
                        </label>
                      </div>

                      {/* Steps Sequence with Drag & Drop Rearranging */}
                      <div className="tuber-idle-steps">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 8,
                          }}
                        >
                          <span className="field-label" style={{ margin: 0 }}>
                            Animation Steps ({idle.steps.length}) · Drag <b>⋮⋮</b> to rearrange:
                          </span>
                          <select
                            className="tuber-add-step-select"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) addIdleStep(idleIdx, e.target.value);
                            }}
                          >
                            <option value="">+ Add step</option>
                            {frames.map((f) => (
                              <option key={f.id} value={f.id}>
                                + {f.id}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tuber-steps-list">
                          {idle.steps.map((step, stepIdx) => (
                            <div
                              key={stepIdx}
                              className={`tuber-step-card ${
                                draggedStepIdx === stepIdx ? "dragging" : ""
                              } ${dragOverStepIdx === stepIdx ? "drag-over" : ""}`}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = "move";
                                setDraggedStepIdx(stepIdx);
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                                if (dragOverStepIdx !== stepIdx) setDragOverStepIdx(stepIdx);
                              }}
                              onDragLeave={() => {
                                if (dragOverStepIdx === stepIdx) setDragOverStepIdx(null);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                if (draggedStepIdx !== null && draggedStepIdx !== stepIdx) {
                                  reorderIdleSteps(idleIdx, draggedStepIdx, stepIdx);
                                }
                                setDraggedStepIdx(null);
                                setDragOverStepIdx(null);
                              }}
                              onDragEnd={() => {
                                setDraggedStepIdx(null);
                                setDragOverStepIdx(null);
                              }}
                            >
                              <span className="tuber-drag-handle" title="Drag to rearrange">
                                ⋮⋮
                              </span>
                              <div className="tuber-step-num">{stepIdx + 1}</div>
                              <div className="tuber-step-thumb">
                                <img src={getFrameSrc(step.frame)} alt={step.frame} />
                              </div>
                              <div className="tuber-step-details">
                                <select
                                  value={step.frame}
                                  onChange={(e) =>
                                    updateIdleStep(idleIdx, stepIdx, {
                                      frame: e.target.value,
                                    })
                                  }
                                >
                                  {frames.map((f) => (
                                    <option key={f.id} value={f.id}>
                                      {f.id}
                                    </option>
                                  ))}
                                </select>
                                <label className="tuber-step-hold">
                                  <span>Hold:</span>
                                  <input
                                    type="number"
                                    min={0}
                                    max={5000}
                                    step={10}
                                    placeholder="Auto"
                                    value={step.hold_ms || ""}
                                    onChange={(e) =>
                                      updateIdleStep(idleIdx, stepIdx, {
                                        hold_ms: parseFloat(e.target.value) || 0,
                                      })
                                    }
                                  />
                                  <span className="unit">ms</span>
                                </label>
                              </div>
                              <div className="tuber-step-actions">
                                <button
                                  type="button"
                                  className="ghost"
                                  disabled={stepIdx === 0}
                                  onClick={() => reorderIdleSteps(idleIdx, stepIdx, stepIdx - 1)}
                                  title="Move up"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="ghost"
                                  disabled={stepIdx === idle.steps.length - 1}
                                  onClick={() => reorderIdleSteps(idleIdx, stepIdx, stepIdx + 1)}
                                  title="Move down"
                                >
                                  ↓
                                </button>
                                <button
                                  type="button"
                                  className="ghost danger"
                                  onClick={() => removeIdleStep(idleIdx, stepIdx)}
                                  title="Remove step"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: FRAMES LIBRARY */}
          {tab === "frames" && (
            <div className="tuber-tab-pane">
              <div
                className="section-head"
                style={{ marginBottom: 12, alignItems: "center" }}
              >
                <div>
                  <span className="field-label" style={{ fontWeight: 600 }}>
                    Frames ({frames.length} / 32)
                  </span>
                  <div className="hint" style={{ marginTop: 2 }}>Transparent PNGs.</div>
                </div>
                <label className="button-like">
                  <input
                    type="file"
                    accept="image/png"
                    hidden
                    disabled={uploading || frames.length >= 32}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      void uploadNewFrame(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                  {uploading ? "Uploading…" : "+ Add New Frame"}
                </label>
              </div>

              <div className="tuber-frames-grid">
                {frames.map((frame) => (
                  <div className="tuber-frame-card" key={frame.id}>
                    <div className="tuber-frame-img-box">
                      <img src={getFrameSrc(frame.id)} alt={frame.id} />
                      <span
                        className={`tuber-frame-badge ${
                          frame.custom ? "custom" : "bundled"
                        }`}
                      >
                        {frame.custom ? "custom" : "bundled"}
                      </span>
                    </div>
                    <div className="tuber-frame-info">
                      <span className="tuber-frame-name">{frame.id}</span>
                    </div>
                    <div className="tuber-frame-btns">
                      <label className="ghost-btn-label">
                        <input
                          type="file"
                          accept="image/png"
                          hidden
                          onChange={(e) => {
                            void replaceFrame(frame.id, e.target.files?.[0]);
                            e.target.value = "";
                          }}
                        />
                        Replace
                      </label>
                      {(frame.custom || (frame.id !== "closed" && frame.id !== "open")) && (
                        <button
                          type="button"
                          className="ghost danger"
                          onClick={() => void deleteOrResetFrame(frame.id)}
                        >
                          {frame.id === "closed" || frame.id === "open"
                            ? "Reset"
                            : "Delete"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Interactive Live Preview Animator Component
// -----------------------------------------------------------------------------

function TuberLivePreview({
  animation,
  frames,
  getFrameSrc,
  testIdleAction,
  onTestIdleDone,
}: {
  animation: TuberAnimation;
  frames: FrameState[];
  getFrameSrc: (id: string) => string;
  testIdleAction: TuberIdle | null;
  onTestIdleDone: () => void;
}) {
  const [simulatingSpeech, setSimulatingSpeech] = useState(false);
  const [simulatingIdles, setSimulatingIdles] = useState(true);
  const [currentFrame, setCurrentFrame] = useState<string>(
    animation.rest_frame || "closed",
  );
  const [transform, setTransform] = useState<string>("none");
  const [statusText, setStatusText] = useState<string>("Resting");

  // Animation simulator loop refs
  const stateRef = useRef({
    simulatingSpeech: false,
    simulatingIdles: true,
    animation,
    frames,
    talkIndex: 0,
    talkUntil: 0,
    idleState: null as {
      steps: TuberStep[];
      step_ms: number;
      index: number;
      until: number;
      name: string;
    } | null,
    nextIdleAt: 0,
    lastTick: performance.now(),
    audioRms: 0,
    audioPhase: 0,
    breathMix: 1,
  });

  stateRef.current.simulatingSpeech = simulatingSpeech;
  stateRef.current.simulatingIdles = simulatingIdles;
  stateRef.current.animation = animation;
  stateRef.current.frames = frames;

  // Trigger manual test idle
  useEffect(() => {
    if (!testIdleAction || testIdleAction.steps.length === 0) return;
    const now = performance.now();
    const firstStep = testIdleAction.steps[0];
    const hold =
      firstStep.hold_ms > 0 ? firstStep.hold_ms : testIdleAction.step_ms || 90;
    stateRef.current.idleState = {
      steps: testIdleAction.steps,
      step_ms: testIdleAction.step_ms || 90,
      index: 0,
      until: now + Math.max(16, hold),
      name: testIdleAction.name || "Idle",
    };
  }, [testIdleAction]);

  useEffect(() => {
    let animId: number;

    const scheduleNextIdle = (now: number) => {
      const min = Math.max(200, stateRef.current.animation.idle_min_ms);
      const max = Math.max(min, stateRef.current.animation.idle_max_ms);
      stateRef.current.nextIdleAt = now + min + Math.random() * (max - min);
    };

    scheduleNextIdle(performance.now());

    function tick(now: number) {
      animId = requestAnimationFrame(tick);
      const s = stateRef.current;
      const anim = s.animation;

      let speaking = false;
      let calculatedLevel = 0;

      // Simulate voice loudness & syllables if test talking is active
      if (s.simulatingSpeech) {
        s.audioPhase += 0.08;
        const wave =
          Math.sin(s.audioPhase) * 0.4 +
          Math.sin(s.audioPhase * 2.3) * 0.3 +
          Math.sin(s.audioPhase * 4.7) * 0.3;
        calculatedLevel = Math.max(0, wave);
        speaking = calculatedLevel > 0.15;
      }

      // 1. Advance active idle animation
      let idleFrame: string | null = null;
      if (s.idleState) {
        const curIdle = s.idleState;
        if (now >= curIdle.until) {
          curIdle.index += 1;
          if (curIdle.index >= curIdle.steps.length) {
            s.idleState = null;
            scheduleNextIdle(now);
            onTestIdleDone();
          } else {
            const step = curIdle.steps[curIdle.index];
            const hold = step.hold_ms > 0 ? step.hold_ms : curIdle.step_ms;
            curIdle.until = now + Math.max(16, hold);
          }
        }
        if (s.idleState) {
          idleFrame = s.idleState.steps[s.idleState.index].frame;
          setStatusText(`Idle: ${s.idleState.name}`);
        }
      } else if (
        s.simulatingIdles &&
        !speaking &&
        now >= s.nextIdleAt &&
        anim.idles.length > 0
      ) {
        // Pick random idle by weight
        const eligible = anim.idles.filter((i) => i.weight > 0 && i.steps.length > 0);
        if (eligible.length > 0) {
          const totalWeight = eligible.reduce((acc, i) => acc + i.weight, 0);
          let roll = Math.random() * totalWeight;
          let chosen = eligible[0];
          for (const item of eligible) {
            roll -= item.weight;
            if (roll <= 0) {
              chosen = item;
              break;
            }
          }
          const firstStep = chosen.steps[0];
          const hold = firstStep.hold_ms > 0 ? firstStep.hold_ms : chosen.step_ms;
          s.idleState = {
            steps: chosen.steps,
            step_ms: chosen.step_ms || 90,
            index: 0,
            until: now + Math.max(16, hold),
            name: chosen.name || "Idle",
          };
          idleFrame = firstStep.frame;
          setStatusText(`Idle: ${chosen.name}`);
        } else {
          scheduleNextIdle(now);
        }
      }

      // 2. Resolve frame
      let nextFrame = idleFrame;
      if (!nextFrame) {
        if (speaking && anim.talk_frames.length > 0) {
          if (anim.talk_mode === "loudness") {
            const idx = Math.min(
              anim.talk_frames.length - 1,
              Math.floor(calculatedLevel * anim.talk_frames.length),
            );
            nextFrame = anim.talk_frames[idx]?.frame || anim.talk_frames[0].frame;
            setStatusText(`Talking (${calculatedLevel.toFixed(2)})`);
          } else {
            // Cycle mode
            if (now >= s.talkUntil) {
              s.talkIndex = (s.talkIndex + 1) % anim.talk_frames.length;
              const step = anim.talk_frames[s.talkIndex];
              const hold =
                step.hold_ms > 0 ? step.hold_ms : anim.talk_frame_ms || 140;
              s.talkUntil = now + Math.max(16, hold);
            }
            nextFrame = anim.talk_frames[s.talkIndex]?.frame;
            setStatusText(`Talking (step ${s.talkIndex + 1})`);
          }
        } else {
          nextFrame = anim.rest_frame || "closed";
          if (!s.idleState) {
            setStatusText("Resting (Quiet)");
          }
        }
      }

      // Fallback check if frame exists
      if (!s.frames.some((f) => f.id === nextFrame)) {
        nextFrame = s.frames[0]?.id || "closed";
      }

      setCurrentFrame(nextFrame);
      const loud = speaking ? Math.min(1, calculatedLevel) : 0;
      s.breathMix += ((loud > 0 ? 0 : 1) - s.breathMix) * 0.05;
      // The breath runs by itself whenever the editor is open, so it is the one
      // movement that respects a request for less motion. Speech is only ever
      // simulated because somebody pressed the button to see it.
      const still = reducedMotion.matches && !s.simulatingSpeech;
      setTransform(still ? "none" : motionTransform(anim, now, loud, s.breathMix));
    }

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [onTestIdleDone]);

  return (
    <div className="tuber-preview-container">
      <div className="tuber-preview-canvas">
        <div
          className="tuber-preview-avatar"
          style={{ transform }}
        >
          <img
            src={getFrameSrc(currentFrame)}
            alt={currentFrame}
            className="tuber-preview-img"
          />
        </div>
        <div className="tuber-preview-status-pill">
          <span className="dot on" />
          <b>{currentFrame}</b> · {statusText}
        </div>
      </div>

      <div className="tuber-preview-bar">
        <button
          type="button"
          className={simulatingSpeech ? "active" : "ghost"}
          onClick={() => setSimulatingSpeech(!simulatingSpeech)}
        >
          {simulatingSpeech ? "■ Stop" : "▶ Test talking"}
        </button>
        <button
          type="button"
          className={simulatingIdles ? "active" : "ghost"}
          onClick={() => setSimulatingIdles(!simulatingIdles)}
        >
          {simulatingIdles ? "✓ Idle on" : "Idle off"}
        </button>
      </div>
    </div>
  );
}
